import { siteText } from '../siteText'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Bookmark } from 'lucide-react'
import { api } from '../api'
import { useAsync } from '../hooks'
import type { SourceMoment } from '../types'
import { Button, Notice, formatDuration } from './ui'
import { ProfileAvatar } from './ProfileAvatar'
import { DeleteButton, UndoToast } from './Deletion'
import './Editorial.css'

type PendingMark = { actor_id?: string; id: string; note: string; source_seconds: number | null; clicked_at?: number; clock_uncertainty?: number; local_clock?: { wall: number; monotonic: number; origin: number } }

const momentOutboxes = new Map<string, { pending: { current: PendingMark[] }; marking: { current: boolean } }>()

// Persist before the network operation. A lost response/reload retries the same
// id, while every deliberate new press retains its own timestamp and identity.
export function useMomentMark(sourceId: string, userId = 'current') {
  const storageKey = `motion:pending-marks:${userId}:${sourceId}`
  const active = useRef(true)
  const generation = useRef(0)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [saved, setSaved] = useState('')
  const [pendingCount, setPendingCount] = useState(0)
  const [savedMoment, setSavedMoment] = useState<SourceMoment | null>(null)
  if (!momentOutboxes.has(storageKey)) momentOutboxes.set(storageKey, { pending: { current: [] }, marking: { current: false } })
  const { pending, marking } = momentOutboxes.get(storageKey)!

  const persist = () => {
    localStorage.setItem(storageKey, JSON.stringify(pending.current))
    setPendingCount(pending.current.length)
    window.dispatchEvent(new CustomEvent('motion:pending-marks', { detail: storageKey }))
  }
  async function flush() {
    if (!active.current || marking.current || !pending.current.length) return
    const turn = generation.current
    const current = () => active.current && generation.current === turn
    marking.current = true
    setBusy(true); setError('')
    try {
      while (pending.current.length && current()) {
        const intent = pending.current[0]
        if (intent.local_clock) {
          const sample = intent.local_clock
          const wall = Date.now() / 1000, mono = performance.now() / 1000
          const response = await api.clock(sourceId)
          if (!current()) return
          const elapsed = performance.now() / 1000 - mono
          if (!Number.isFinite(response.server_seconds)) throw new Error('The server clock could not be checked.')
          // A restarted page or adjusted OS clock cannot validate an unsent
          // old wall timestamp; retain it as unresolved intent on the server.
          const continuous = sample.origin === performance.timeOrigin
            && Math.abs(wall - sample.wall - (mono - sample.monotonic)) < .1
            && Math.abs(Date.now() / 1000 - wall - elapsed) < .1
          if (response.frame_clock === false) {
            delete intent.clicked_at
            delete intent.clock_uncertainty
          } else if (continuous && elapsed <= 2) {
            intent.clicked_at = sample.wall + response.server_seconds - wall - elapsed / 2
            intent.clock_uncertainty = elapsed / 2 + .1
          }
          delete intent.local_clock
          persist()
        }
        if (!current()) return
        const marked = await api.markMoment(sourceId, intent)
        if (!current()) return
        pending.current.shift(); persist()
        setSavedMoment(marked)
        setSaved(marked.source_seconds === null
          ? 'Moment intent saved.'
          : `Moment saved at ${formatDuration(marked.source_seconds)}. AI will review the surrounding context.`)
      }
    } catch (e) {
      setError(`${e instanceof Error ? e.message : 'The moment could not be saved.'} ${pending.current.length} pending moment${pending.current.length === 1 ? '' : 's'} kept on this device.`)
    } finally { marking.current = false; setBusy(false) }
  }
  useEffect(() => {
    active.current = true
    generation.current += 1
    try {
      const stored = JSON.parse(localStorage.getItem(storageKey) || '[]')
      pending.current = Array.isArray(stored) ? stored.filter(item => typeof item?.id === 'string' && (item.source_seconds === null || Number.isFinite(item.source_seconds))) : []
      setPendingCount(pending.current.length)
    } catch { pending.current = [] }
    const retry = () => { void flush() }
    const changed = () => setPendingCount(pending.current.length)
    window.addEventListener('motion:pending-marks', changed)
    window.addEventListener('online', retry)
    void flush()
    return () => { active.current = false; generation.current += 1; window.removeEventListener('online', retry); window.removeEventListener('motion:pending-marks', changed) }
  }, [storageKey])
  function mark(live: boolean, seconds: number) {
    const wall = Date.now() / 1000
    const intent: PendingMark = { ...(userId === 'current' ? {} : { actor_id: userId }), id: crypto.randomUUID(), note: '', source_seconds: live ? null : seconds }
    if (live) {
      intent.clicked_at = wall
      intent.local_clock = { wall, monotonic: performance.now() / 1000, origin: performance.timeOrigin }
    }
    pending.current.push(intent)
    try { persist() }
    catch {
      pending.current.pop()
      setError('This device could not retain the moment. Free local storage and try again.')
      return
    }
    setSaved('')
    void flush()
  }
  const acknowledge = useCallback((id: string) => {
    setSavedMoment(current => current?.id === id ? null : current)
  }, [])
  return { busy, error, saved, savedMoment, pendingCount, retry: flush, mark, acknowledge }
}

export function SourceMoments({ sourceId, seconds, live, ready, timelineAvailable = true, marker }: { sourceId: string; seconds: number; live: boolean; ready: boolean; timelineAvailable?: boolean; marker: ReturnType<typeof useMomentMark> }) {
  const moments = useAsync(() => api.moments(sourceId), [sourceId], { pollMs: 4000 })
  const [error, setError] = useState('')
  const { busy, saved, savedMoment, acknowledge } = marker
  const [editing, setEditing] = useState<SourceMoment | null>(null)
  const [note, setNote] = useState('')
  const [saving, setSaving] = useState(false)
  const [changingId, setChangingId] = useState<string | null>(null)
  const [hiddenIds, setHiddenIds] = useState<Set<string>>(() => new Set())
  const [undo, setUndo] = useState<SourceMoment | null>(null)
  const changing = useRef(false)
  const momentList = useRef<HTMLOListElement>(null)
  useEffect(() => {
    const list = momentList.current
    if (!list || typeof ResizeObserver === 'undefined') return
    const rows = [...list.children].slice(0, 3)
    const sizeList = () => { list.style.maxHeight = `${Math.ceil(rows.reduce((height, row) => height + row.getBoundingClientRect().height, 0))}px` }
    const observer = new ResizeObserver(sizeList)
    rows.forEach(row => observer.observe(row))
    sizeList()
    return () => observer.disconnect()
  }, [moments.data, hiddenIds, editing])
  useEffect(() => {
    if (changingId !== null || !moments.data) return
    const listed = new Set(moments.data.map(moment => moment.id))
    setHiddenIds(current => {
      const next = new Set([...current].filter(id => listed.has(id)))
      return next.size === current.size ? current : next
    })
  }, [moments.data, changingId])
  const canMark = live || ready && timelineAvailable && Number.isFinite(seconds) && seconds >= 0
  useEffect(() => {
    if (!savedMoment) return
    moments.setData(current => [...(current || []).filter(item => item.id !== savedMoment.id), savedMoment].sort((a, b) => (a.source_seconds ?? Infinity) - (b.source_seconds ?? Infinity)))
    acknowledge(savedMoment.id)
    void moments.refresh()
  }, [savedMoment, moments.setData, moments.refresh, acknowledge])
  function mark() {
    if (!canMark) return
    setError('')
    void marker.mark(live, seconds)
  }
  async function saveNote() {
    if (!editing || saving || changing.current) return
    setSaving(true); setError('')
    try { await api.updateMoment(sourceId, editing.id, { note, previous_note: editing.note }); await moments.refresh(); setEditing(null) }
    catch (e) { setError(e instanceof Error ? e.message : 'The note could not be saved.') }
    finally { setSaving(false) }
  }
  async function remove(moment: SourceMoment) {
    if (changing.current || saving) return
    changing.current = true; setChangingId(moment.id); setError('')
    try {
      await api.deleteMoment(sourceId, moment.id)
      setHiddenIds(current => new Set(current).add(moment.id))
      moments.setData(current => current?.filter(item => item.id !== moment.id) ?? [])
      if (editing?.id === moment.id) setEditing(null)
      setUndo(moment)
      await moments.refresh()
    } catch (e) { setError(e instanceof Error ? e.message : 'The moment could not be deleted.') }
    finally { changing.current = false; setChangingId(null) }
  }
  async function restore() {
    if (!undo || changing.current || saving) return
    changing.current = true; setChangingId(undo.id); setError('')
    try {
      const restored = await api.restoreMoment(sourceId, undo.id)
      // Let any earlier poll finish before inserting the restored row.
      await moments.refresh()
      moments.setData(current => [...(current ?? []).filter(item => item.id !== restored.id), restored].sort((a, b) => (a.source_seconds ?? Infinity) - (b.source_seconds ?? Infinity)))
      setHiddenIds(current => { const next = new Set(current); next.delete(restored.id); return next })
      setUndo(null)
    } catch (e) { setError(e instanceof Error ? e.message : 'The moment could not be restored.') }
    finally { changing.current = false; setChangingId(null) }
  }
  const visibleMoments = moments.data?.filter(moment => !hiddenIds.has(moment.id)) ?? []
  return <section className="moment-strip" aria-label="Producer moments">
    <Button variant="primary" disabled={!canMark} onClick={() => void mark()}><Bookmark size={17} /> Mark Moment</Button>
    {marker.pendingCount ? <small role="status">{marker.pendingCount} moment{marker.pendingCount === 1 ? '' : 's'} pending confirmation. {busy ? 'Saving…' : <button type="button" onClick={() => void marker.retry()}>Retry Pending</button>}</small> : null}
    {saved ? <small role="status">{siteText(saved)}</small> : null}
    {error || marker.error || moments.error ? <Notice>{siteText(error || marker.error || moments.error)}</Notice> : null}
    {visibleMoments.length ? <ol ref={momentList} aria-label="Marked Moments" tabIndex={0}>{[...visibleMoments].reverse().map(moment => <li key={moment.id}>
      <div className="moment-row">
        <ProfileAvatar className="moment-avatar" name={moment.actor?.name || 'Producer'} photo={moment.actor?.avatar_url || (moment.actor?.email.toLowerCase() === 'will@willweinbach.com' ? '/avatars/will.png' : null)} />
        <div className="moment-content"><p><strong>{siteText(moment.actor?.name || 'Producer')}</strong> marked a moment.</p>{moment.note ? <p className="moment-note">{siteText(moment.note)}</p> : null}</div>
        <time>{siteText(moment.source_seconds === null ? (moment.clock?.review_required ? 'Time needs review' : 'Time pending verification') : formatDuration(moment.source_seconds))}</time><button type="button" className="page-text-action" disabled={saving || changingId !== null} onClick={() => { setEditing(moment); setNote(moment.note) }}>{siteText(moment.note ? 'Edit Note' : 'Add Note')}</button><DeleteButton label={`moment at ${moment.source_seconds === null ? (moment.clock?.review_required ? 'Time needs review' : 'Time pending verification') : formatDuration(moment.source_seconds)}`} busy={changingId === moment.id} disabled={saving || changingId !== null} onClick={() => void remove(moment)} /></div>
      {editing?.id === moment.id ? <form onSubmit={e => { e.preventDefault(); void saveNote() }}><input aria-label={siteText(`Note for moment at ${moment.source_seconds === null ? (moment.clock?.review_required ? 'Time needs review' : 'Time pending verification') : formatDuration(moment.source_seconds)}`)} value={note} maxLength={1000} onChange={e => setNote(e.target.value)} autoFocus /><div className="moment-note-actions"><Button size="sm" busy={saving} disabled={changingId !== null} type="submit">Save Note</Button><button className="page-text-action" type="button" disabled={saving || changingId !== null} onClick={() => setEditing(null)}>Cancel</button></div></form> : null}
    </li>)}</ol> : null}
    {undo ? <UndoToast key={undo.id} message={`Moment at ${undo.source_seconds === null ? 'time pending verification' : formatDuration(undo.source_seconds)} deleted.`} onUndo={() => void restore()} onDismiss={() => setUndo(null)} busy={changingId !== null} /> : null}
  </section>
}
